import { useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';
import type { RootState } from 'src/root/store';

import { ProgramId, ranksLevels } from '@shared/constants';
import {
  hasNewPermissionSel,
  isYouthMemberSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';
import { RankModel } from '@shared/utils/ranksModel';

import { AdvancementAction } from '../components/AdvancementDetails/types';
import type {
  SupportedAdvancementType,
  YouthAllowedActionsResult,
} from '../components/AdvancementDetails/types';

/** The four youth per-rank editor/approver permissions */
export interface EditApprovePerms {
  scout: boolean;
  tenderfoot: boolean;
  secondClass: boolean;
  firstClass: boolean;
}

export interface YouthActionPolicyInput {
  /** Scouts BSA rank level (1-7) of the advancement, else undefined (non-rank type) */
  rankLevel?: number;
  editApprove: EditApprovePerms;
}

/** Every control the card can offer; a youth is denied all of them at header level */
const ALL_ACTIONS: AdvancementAction[] = [
  AdvancementAction.CompletionDate,
  AdvancementAction.Approve,
  AdvancementAction.Award,
  AdvancementAction.CounselorApprove,
];

/**
 * Pure policy for a youth viewer, resolved separately for the advancement itself and for the
 * requirements inside it.
 *
 * A youth NEVER edits an advancement at header level - setting a rank's own completion date
 * is awarding oneself the rank, whatever tier is held - so `disabledActions` is every action,
 * unconditionally.
 *
 * `requirementDisabledActions` is where the tier applies. The `EDIT_APPROVE_*` grants are
 * cumulative (First Class covers all four ranks, Scout covers only Scout); within that tier a
 * youth may set a requirement's date and approve it, and never award or counselor-approve.
 * Off-rank types and Star/Life/Eagle are never covered.
 */
export function resolveYouthActions(
  input: YouthActionPolicyInput,
): YouthAllowedActionsResult {
  const { rankLevel, editApprove } = input;

  const hasAnyRankApprove =
    editApprove.scout ||
    editApprove.tenderfoot ||
    editApprove.secondClass ||
    editApprove.firstClass;

  // Cumulative: a higher tier covers the ranks below it. Star/Life/Eagle fall to
  // `default`; no youth tier reaches them, and non-rank types carry no level
  const coversThisRank = (() => {
    switch (rankLevel) {
      case ranksLevels.SCOUT:
        return hasAnyRankApprove;
      case ranksLevels.TENDERFOOT:
        return (
          editApprove.tenderfoot ||
          editApprove.secondClass ||
          editApprove.firstClass
        );
      case ranksLevels.SECOND_CLASS:
        return editApprove.secondClass || editApprove.firstClass;
      case ranksLevels.FIRST_CLASS:
        return editApprove.firstClass;
      default:
        return false;
    }
  })();

  const disabled = new Set<AdvancementAction>();
  // Date + approve follow the tier; award and counselor-approve are never a youth's to give
  if (!coversThisRank) {
    disabled.add(AdvancementAction.CompletionDate);
    disabled.add(AdvancementAction.Approve);
  }
  disabled.add(AdvancementAction.Award);
  disabled.add(AdvancementAction.CounselorApprove);

  return {
    disabledActions: [...ALL_ACTIONS],
    requirementDisabledActions: [...disabled],
  };
}

interface UseYouthAdvAllowedActionsArgs {
  type?: SupportedAdvancementType;
  advancementId?: number | string;
}

/**
 * Resolves which actions the current youth viewer may take, so the page can hand
 * `disabledActions` to `AdvancementDetails` and `requirementDisabledActions` to the
 * requirements checklist, and both containers stay policy-free. For youth surfaces only
 * (the roster-lite access gate guarantees a youth viewer); leaders use
 * `useLeaderAdvAllowedActions`. Warns if mounted for a non-youth so the mistake isn't silent
 */
export function useYouthAdvAllowedActions(
  args: UseYouthAdvAllowedActionsArgs,
): YouthAllowedActionsResult {
  const { type, advancementId } = args;

  const isYouth = useSelector(isYouthMemberSel);
  const scout = useSelector((s: RootState) =>
    hasNewPermissionSel(s, newPermissions.EDIT_APPROVE_SCOUT),
  );
  const tenderfoot = useSelector((s: RootState) =>
    hasNewPermissionSel(s, newPermissions.EDIT_APPROVE_TENDERFOOT),
  );
  const secondClass = useSelector((s: RootState) =>
    hasNewPermissionSel(s, newPermissions.EDIT_APPROVE_SECOND_CLASS),
  );
  const firstClass = useSelector((s: RootState) =>
    hasNewPermissionSel(s, newPermissions.EDIT_APPROVE_FIRST_CLASS),
  );

  // Only Scouts BSA ranks map to these permissions; Cub rank levels collide with
  // them (Wolf === Second Class === 3), so gate on the program
  const rankLevel = useMemo(() => {
    if (type !== 'ranks' || advancementId == null) return undefined;
    const rankId = Number(advancementId);
    if (RankModel.getProgramIdForRank(rankId) !== ProgramId.BOY_SCOUT) {
      return undefined;
    }
    try {
      return new RankModel(ProgramId.BOY_SCOUT, rankId).level;
    } catch {
      return undefined;
    }
  }, [type, advancementId]);

  // Youth-only invariant: the access gate should guarantee a youth viewer. If it doesn't,
  // log it so the mistake isn't silent (the returned policy would tier-gate a leader)
  useEffect(() => {
    if (!isYouth) {
      console.warn(
        'useYouthAdvAllowedActions requires a youth viewer; leaders must use useLeaderAdvAllowedActions',
      );
    }
  }, [isYouth]);

  return useMemo(
    () =>
      resolveYouthActions({
        rankLevel,
        editApprove: { scout, tenderfoot, secondClass, firstClass },
      }),
    [rankLevel, scout, tenderfoot, secondClass, firstClass],
  );
}
